import { setCookie, type_CD_STATE, type_GM_STATE, getCookie, setInStorage, removeFromStorage } from './../../helpers/index'
import { OmneoAuthTokenResError, OmneoAuthTokenResSuccess } from '../types'
import { OmneoService } from './omneo.service'
import { tokenNames } from './utils'

/* @ts-ignore */
import ShapesSDK from '@omneo/shapes-sdk'

declare global {
  var CD_STATE: type_CD_STATE
  var GM_STATE: type_GM_STATE
  interface Window {
    omneoService: OmneoService
  }
}

export const initOmneo = async () => {
  const keyTypes: ('public' | 'private')[] = ['public', 'private']
  const promises = keyTypes.map((keyType) => getOmneoToken(keyType))
  const tokens = await Promise.allSettled(promises)
  const rejectedTokens = tokens.filter((token) => token.status === 'rejected') as PromiseRejectedResult[]
  if (rejectedTokens.length > 0) {
    console.error('Cannot construct omneoService without a token', {
      rejected: rejectedTokens.map((token) => token.reason),
      tokens
    })
    return
  }
  const resolvedTokens = tokens.filter((token) => token.status === 'fulfilled') as PromiseFulfilledResult<string>[]
  const [publicToken, privateToken] = resolvedTokens.map((token) => token.value)
  window.omneoService = new OmneoService(privateToken, publicToken)
}

// TODO: move to utils
function delayGetToken(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const getOmneoToken = async (keyType: 'public' | 'private' = 'private'): Promise<string> => {
  // view=wishlist&form=register'
  // if the customer has just registered, wait 2 seconds.
  // this seems to be an issue with the omneo SDK that when the new customer is created the token is not immediately available
  const currentUrl = new URL(window.location.toString())
  const fromForm = currentUrl.searchParams.has('form') || currentUrl.searchParams.has('register')
  if (fromForm) {
    await delayGetToken(2000)
  }

  if (keyType === 'private') {
    const preExistingToken = getCookie(tokenNames.omneo.cookie)

    if (!preExistingToken) {
      Object.keys(localStorage)
        .filter((key) => key.includes(tokenNames.omneo.localStorageIdBase))
        .forEach((key) => {
          removeFromStorage(key, 'local')
        })
    }

    if (preExistingToken) {
      const key = tokenNames.omneo.localStorageIdBase + CD_STATE.omneo.customerId
      const value = preExistingToken
      setInStorage(key, value, 'local')
      return preExistingToken
    }
  }

  const omneoProps = CD_STATE.omneo

  const url = omneoProps.authUrl
  const config = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      id: '',
      signature: omneoProps.publicSignature
    })
  }
  if (keyType === 'private') {
    config.body = JSON.stringify({
      id: omneoProps.customerId || '',
      signature: omneoProps.signature
    })
  }

  let res
  let data: OmneoAuthTokenResSuccess | OmneoAuthTokenResError | undefined

  try {
    res = await fetch(url, config)
  } catch (err) {
    console.error('Network error while attempting to get omneo token', url, config, err)
    // need to add some actual error handling here
    return ''
  }

  try {
    data = await res.json()
  } catch (err) {
    console.error('Parsing error while trying to get omneo token', url, config, err)
    // need to add some actual error handling here
    return ''
  }

  if ((data as OmneoAuthTokenResError).error) {
    console.error('Error generating omneo token', data)
    return ''
  }

  const { token, exp } = (data as OmneoAuthTokenResSuccess).data

  // browser uses millisecond precision, omneo returns second precision
  let addMilliseconds = false
  if (Date.now().toString().length - exp.toString().length === 3) {
    addMilliseconds = true
  }

  const key = tokenNames.omneo.localStorageIdBase + CD_STATE.omneo.customerId

  if (keyType === 'private') {
    setInStorage(key, token, 'local')
    setCookie({
      name: tokenNames.omneo.cookie,
      value: token,
      unixTimestamp: addMilliseconds ? `${exp}000` : exp
    })
  }
  return token
}

// TODO: this function didnt end up working and reverted to the standard delay before the intial fetch
// API might not have created a token for the new user on register right away so we need to retry until we have both the public and private tokens
async function fetchWithBackoff(maxRetries = 5): Promise<{ privateToken: string, publicToken: string }> {
  let attempt = 0;
  const userLoggedIn = GM_STATE?.customer?.logged || false

  while (attempt < maxRetries) {
    const keyTypes: ('public' | 'private')[] = ['public', 'private']
    const promises = keyTypes.map((keyType) => getOmneoToken(keyType))
    const tokens = await Promise.allSettled(promises)
    const rejectedTokens = tokens.filter((token) => token.status === 'rejected') as PromiseRejectedResult[]

    if (rejectedTokens.length > 0) {
      console.error('Cannot construct omneoService without a token', {
        rejected: rejectedTokens.map((token) => token.reason),
        tokens
      })
      throw new Error('No tokens returned from wishlist service');
    }
    const resolvedTokens = tokens.filter((token) => token.status === 'fulfilled') as PromiseFulfilledResult<string>[]
    const [publicToken, privateToken] = resolvedTokens.map((token) => token.value)

    // Check if the response is valid
    // only care if the tokens dont matched if the user is logged in
    if (userLoggedIn && publicToken !== privateToken) {
      return { privateToken, publicToken }
    }

    // Exponential backoff delay
    const delay = Math.pow(2, attempt) * 1000; // 1s, 2s, 4s, 8s, ...
    await new Promise((resolve) => setTimeout(resolve, delay));

    attempt++;
  }

  throw new Error('Maximum retries reached without a valid response.');
}